import { supabase } from '@/lib/supabase';

// ============================================================
// Catégories de thèmes (table `typetheme`)
// Chaque catégorie porte un « fichier de référence » : les
// instructions transmises à l'IA pour convertir un thème de
// ce type. Les instructions sont stockées en texte directement
// en base (pas de stockage binaire nécessaire).
// ============================================================

export interface ThemeCategory {
  id: number;
  titre: string;
  reference_instructions?: string | null;
  reference_file_name?: string | null;
  source_language?: string | null;
}

/** Types de langage source supportés par l'IA de conversion. */
export type CategorySourceLanguage = 'auto' | 'html' | 'php' | 'php+html';

export const CATEGORY_LANGUAGE_LABELS: Record<CategorySourceLanguage, string> = {
  auto: 'Détection automatique',
  html: 'HTML',
  php: 'PHP',
  'php+html': 'PHP + HTML',
};

export async function fetchThemeCategories(): Promise<ThemeCategory[]> {
  const { data, error } = await supabase.from('typetheme').select('*').order('id');
  if (error) throw new Error(error.message);
  return (data || []) as ThemeCategory[];
}

export async function createThemeCategory(titre: string): Promise<ThemeCategory> {
  const clean = titre.trim();
  if (!clean) throw new Error('Le titre de la catégorie est obligatoire.');
  const { data, error } = await supabase
    .from('typetheme')
    .insert({ titre: clean, source_language: 'auto' })
    .select('*')
    .single();
  if (error) throw new Error(error.message);
  return data as ThemeCategory;
}

export async function updateThemeCategoryTitle(id: number, titre: string): Promise<void> {
  const clean = titre.trim();
  if (!clean) throw new Error('Le titre de la catégorie est obligatoire.');
  const { error } = await supabase.from('typetheme').update({ titre: clean }).eq('id', id);
  if (error) throw new Error(error.message);
}

export async function updateThemeCategoryReference(
  id: number,
  referenceInstructions: string,
  referenceFileName: string,
  sourceLanguage: CategorySourceLanguage = 'auto',
): Promise<void> {
  const { error } = await supabase
    .from('typetheme')
    .update({
      reference_instructions: referenceInstructions,
      reference_file_name: referenceFileName,
      source_language: sourceLanguage,
    })
    .eq('id', id);
  if (error) throw new Error(error.message);
}

export async function deleteThemeCategory(id: number): Promise<void> {
  const { error } = await supabase.from('typetheme').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

/**
 * Compte le nombre de thèmes rattachés à chaque catégorie.
 * La colonne `sitewebtheme.typetheme` stocke l'id de catégorie sous forme de texte.
 */
export async function fetchThemeCountsByCategory(): Promise<Record<string, number>> {
  const { data, error } = await supabase.from('sitewebtheme').select('typetheme');
  if (error) throw new Error(error.message);
  const counts: Record<string, number> = {};
  (data || []).forEach((row: { typetheme?: string | null }) => {
    const key = String(row.typetheme ?? '').trim();
    if (!key) return;
    counts[key] = (counts[key] || 0) + 1;
  });
  return counts;
}

/** Lit un fichier de référence (.txt/.md/.html/.json) côté navigateur. */
export async function readReferenceFile(file: File): Promise<string> {
  return file.text();
}